import { CloseOutlined } from '@ant-design/icons'
import { SearchOutlined } from '@mui/icons-material'
import React from 'react'
import styled from 'styled-components'
import { Input as SutraInput, InputProps } from 'sutra/components/Input/Input'
import { gray } from 'sutra/tokens/colors'
import { radii } from 'sutra/tokens/radii'

export type SearchSize = 'sm' | 'md'

const heightBySize: Record<SearchSize, string> = {
  sm: '32px',
  md: '36px',
}

type Props = Omit<InputProps, 'onChange' | '$size' | '$height' | 'size'> & {
  onChange: React.ChangeEventHandler<HTMLInputElement>
  onClear?: () => void
  /** Renders a <kbd> hint (e.g. "/") at the right edge while empty and unfocused. */
  shortcutHint?: string
  /** Field height. Defaults to 'sm' (32px). */
  size?: SearchSize
}

export const SutraSearch = React.forwardRef<HTMLInputElement, Props>(
  (
    { onChange, onClear, shortcutHint, size = 'sm', value, className, ...rest },
    ref,
  ) => {
    const handleClear = (): void => {
      if (onClear) {
        onClear()
      } else {
        const syntheticEvent = {
          target: { value: '' },
        } as React.ChangeEvent<HTMLInputElement>
        onChange(syntheticEvent)
      }
    }

    return (
      <Field className={className}>
        <Icon>
          <SearchOutlined style={{ fontSize: 14 }} />
        </Icon>
        <StyledInput
          ref={ref}
          $size="sm"
          $height={heightBySize[size]}
          value={value}
          onChange={onChange}
          {...rest}
        />
        {value ? (
          <ClearButton onClick={handleClear}>
            <CloseOutlined />
          </ClearButton>
        ) : (
          shortcutHint && <ShortcutHint>{shortcutHint}</ShortcutHint>
        )}
      </Field>
    )
  },
)

SutraSearch.displayName = 'SutraSearch'

const Field = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`

const Icon = styled.span`
  position: absolute;
  left: 10px;
  display: flex;
  align-items: center;
  color: #9fb8c3;
  pointer-events: none;
`

const StyledInput = styled(SutraInput)`
  padding: 0 36px;
`

const ClearButton = styled.span`
  position: absolute;
  right: 10px;
  display: flex;
  align-items: center;
  color: ${gray[500]};
  cursor: pointer;
`

const ShortcutHint = styled.kbd`
  position: absolute;
  right: 10px;
  display: flex;
  align-items: center;
  font-size: 11px;
  font-family: monospace;
  font-weight: normal;
  color: ${gray[500]};
  background: ${gray[100]};
  border-radius: ${radii.xs}px;
  padding: 1px 5px;
  pointer-events: none;

  ${Field}:focus-within & {
    opacity: 0;
  }
`
